import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const aiData = {
  hero: {
    badge: "AI Development",
    headline: "AI-Powered Digital Solutions",
    subtext: "Integrate modern AI capabilities into your applications, SaaS platforms and business workflows.",
    primaryCTA: "Explore AI Solutions",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "AI API Integration", desc: "Connect OpenAI, Claude and other AI services to your applications.", icon: "ri-exchange-line" },
    { title: "ChatGPT Integration", desc: "Intelligent chat, content generation and automation using GPT models.", icon: "ri-chat-3-line" },
    { title: "Claude Integration", desc: "Advanced reasoning, analysis and document processing with Anthropic Claude.", icon: "ri-bard-line" },
    { title: "AI Chatbots", desc: "Conversational AI for customer support, sales and internal tools.", icon: "ri-robot-2-line" },
    { title: "AI Assistants", desc: "Personalized AI assistants for productivity and decision support.", icon: "ri-user-settings-line" },
    { title: "Workflow Automation", desc: "Automate repetitive tasks with AI-powered workflows and triggers.", icon: "ri-settings-3-line" },
    { title: "AI-powered SaaS", desc: "Add AI features to your SaaS product for competitive advantage.", icon: "ri-cloud-line" },
    { title: "AI Content Automation", desc: "Automated content creation, summarization and translation.", icon: "ri-article-line" },
    { title: "AI Business Automation", desc: "Intelligent data processing, classification and prediction systems.", icon: "ri-brain-line" },
  ],
  techStack: [
    { name: "OpenAI", icon: "ri-openai-line" },
    { name: "Claude", icon: "ri-bard-line" },
    { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", featured: true },
    { name: "REST APIs", icon: "ri-exchange-line" },
    { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
    { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg" },
  ],
  architecture: {
    layers: [
      { name: "User Interface", icon: "ri-computer-line", desc: "Web / Mobile / Chat Interface" },
      { name: "Laravel Application", icon: "ri-code-s-slash-line", desc: "Controllers / Middleware / Queues" },
      { name: "AI Service Layer", icon: "ri-brain-line", desc: "Prompt Engineering / Context / Caching" },
      { name: "AI Models", icon: "ri-bard-line", desc: "OpenAI / Claude / Gemini" },
      { name: "Database", icon: "ri-database-2-line", desc: "MySQL / PostgreSQL" },
      { name: "Vector Store", icon: "ri-server-line", desc: "Pinecone / Weaviate / Redis" },
    ],
  },
  aiTech: [
    { name: "ChatGPT", desc: "Conversational AI, content generation, code assistance", icon: "ri-chat-3-line" },
    { name: "Claude", desc: "Advanced reasoning, analysis and long-context processing", icon: "ri-bard-line" },
    { name: "Claude Code", desc: "AI-powered development and code review assistance", icon: "ri-code-line" },
    { name: "Gemini", desc: "Google AI for multimodal understanding and search", icon: "ri-google-line" },
    { name: "GitHub Copilot", desc: "AI pair programmer for faster development", icon: "ri-github-line" },
    { name: "Cursor", desc: "AI-first code editor with intelligent completions", icon: "ri-terminal-line" },
    { name: "OpenAI Codex", desc: "Code generation and technical reasoning", icon: "ri-code-s-slash-line" },
  ],
  faq: [
    { q: "How do you integrate AI into existing applications?", a: "We add AI capabilities through REST API integrations, building service layers that handle prompt engineering, context management and response processing." },
    { q: "Is my data safe when using AI APIs?", a: "Yes. We implement data anonymization, local caching, encryption and follow provider privacy policies. Sensitive data can be processed in isolated environments." },
    { q: "Can AI replace human decision-making?", a: "We design AI as an augmentation tool that provides recommendations and insights while keeping humans in control of critical decisions." },
    { q: "What are the costs of AI integration?", a: "Costs depend on usage volume, model selection and implementation complexity. We help optimize token usage and caching to control costs." },
  ],
};

export default function AiDevelopmentPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "AI Development" },
        ]}
      />

      <PageHero
        badge={aiData.hero.badge}
        headline={aiData.hero.headline}
        subtext={aiData.hero.subtext}
        primaryCTA={aiData.hero.primaryCTA}
        secondaryCTA={aiData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">AI Development Services</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {aiData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">AI Technologies We Use</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {aiData.aiTech.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${tech.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{tech.name}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{tech.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">AI + Laravel Architecture</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              {aiData.architecture.layers.map((layer, i) => (
                <AnimatedSection key={layer.name} delay={i * 120}>
                  <div className="relative">
                    {i > 0 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-gradient-to-b from-transparent to-accent-500/40" />}
                    <div className="flex items-center gap-4 px-6 py-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 w-[280px] md:w-[360px]">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                        <i className={`${layer.icon} text-lg`} />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 font-heading">{layer.name}</h4>
                        <p className="text-xs text-foreground-500">{layer.desc}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          <AnimatedSection delay={800}>
            <p className="text-center text-sm text-foreground-600 mt-10 max-w-2xl mx-auto leading-relaxed">
              Laravel applications can be extended with intelligent AI-powered workflows, assistants and automation. We build service layers that securely connect your Laravel backend to AI models, enabling features like content generation, data analysis, chatbots and intelligent recommendations.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <FAQSection heading="AI Development FAQ" items={aiData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}